import { toast } from 'react-toastify'
import { encodeEventTopics } from 'viem'
import { useChainId, useSignTypedData, useSwitchChain, useWriteContract } from 'wagmi'

import { apiNewPostReceiveCctpMessage, apiSendOrderData } from '@src/api/order.api'
import { WRAPPED_GAS_TOKENS, getGasTokenSymbol, getChainNameFromChainId } from '@src/configs/chainInfo'
import { ExchangeType, LiquiditySource, ResultCode, StepNames } from '@src/enums'
import { useApproveToken } from '@src/hooks/evm/useApproveToken'
import { useDepositWeth } from '@src/hooks/evm/useDepositWeth'
import { useWithdrawWeth } from '@src/hooks/evm/useWithdrawWeth'
import { analyticsService } from '@src/services/analytics'
import { useWaitForTransaction } from '@src/hooks/useWaitForTransaction'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import { useExchangeDataContext } from '@src/pages/Exchange/context/ExchangeDataContext'
import { ChainHandler, EvmSwapParams, IExchangeData, UpdateData } from '@src/types/exchange'
import { IQuoteData } from '@src/types/order'
import { isFeatureEnabled } from '@src/utils'
import { getChecksumAddress, isEVMAddress } from '@src/utils/address'
import { prepareCctpTypedData, prepareOrderBookTypedData, supportsEip712 } from '@src/utils/eip712'
import { getSwapFunctionParameters } from '@src/utils/exchangeUtils'
import { getAllowance } from '@src/utils/getAllowance'
import { getBlockExplorerUrlFromChainId } from '@src/utils/getBlockExplorerUrl'
import { createContextLogger } from '@src/utils/logger'
import { processReceipt } from '@src/utils/processReceipt'
import { mapTokenAddress } from '@src/utils/tokenAddressMapping'
import { useConfigStore } from '@src/zustand_stores'
import { useApplicationStore } from '@src/zustand_stores/application.store'
import { useHistoryStore } from '@src/zustand_stores/history.store'

const logger = createContextLogger('useExchangeTokenEvm')

export function useExchangeTokenEvm(swapContract: any): ChainHandler {
    const referralCode = useApplicationStore(state => state.generatedReferralCode)
    const { setOrderId, setWaitingForConnectedWalletInteraction } = useApplicationStore()
    const availableNetworks = useConfigStore(config => config.availableNetworks)
    const { approveToken, waitForApproval } = useApproveToken()
    const { depositWeth, waitForDeposit } = useDepositWeth()
    const { withdrawWeth, waitForWithdraw } = useWithdrawWeth()
    const { waitForTransactionReceipt } = useWaitForTransaction()
    const { writeContractAsync } = useWriteContract()
    const { signTypedDataAsync } = useSignTypedData()
    const updateHistory = useHistoryStore(state => state.updateHistory)
    const { destinationAddress } = useExchangeDataContext()
    const chainId = useChainId()
    const { switchChainAsync } = useSwitchChain()
    const { solanaWalletAddress } = useWalletConnections()

    /**
     * Helper function to ensure the user is on the correct chain before performing operations
     * @param targetChainId The chain ID that the operation requires
     * @param operationType The type of operation (for logging and error messages)
     * @param updateData Data for updating the history store
     * @param currentStep The current step in the process
     * @returns Promise that resolves when chain switching is complete
     */
    const ensureCorrectChain = async (
        targetChainId: number,
        operationType: string,
        updateData: UpdateData | null,
        currentStep: StepNames
    ): Promise<void> => {
        // Check if we need to switch chains
        if (chainId !== targetChainId) {
            logger.info(`Switching chain from ${chainId} to ${targetChainId} for ${operationType} operation`)

            // Update history if updateData is provided
            if (updateData) {
                updateHistory(null, {
                    ...updateData,
                    currentStep,
                    result: {
                        ...updateData.result,
                        message: `Switching to ${availableNetworks[targetChainId]?.name || targetChainId} network...`,
                    },
                })
            }

            try {
                await switchChainAsync({ chainId: targetChainId })
                logger.success(`Successfully switched to chain ${targetChainId}`)
            } catch (switchError: any) {
                // Get the chain name for a user-friendly error message
                const targetChainName = availableNetworks[targetChainId]?.name || `Chain ID ${targetChainId}`

                // Log the network switch failure
                logger.error(`Failed to switch chain for ${operationType} operation`, {
                    from: chainId,
                    to: targetChainId,
                    targetChainName,
                    isTestnetEnvironment: isFeatureEnabled('USE_TESTNETS_ONLY'),
                    error: switchError,
                })

                // Show a user-friendly error toast
                toast.error(`Please switch to ${targetChainName} network to continue.`, {
                    position: 'bottom-right',
                    autoClose: 7000,
                })

                throw new Error(`Please switch to ${targetChainName} network to ${operationType}.`)
            }
        }
    }

    const handleTokenApproval = async (
        tokenAddress: string,
        spenderAddress: string,
        amount: bigint,
        updateData: UpdateData,
        sellData: IExchangeData
    ): Promise<string> => {
        logger.info('Initiating token approval', {
            tokenAddress,
            spenderAddress,
            amount,
        })

        try {
            logger.info('[handleTokenApproval]', tokenAddress, spenderAddress, amount)
            const hash = await approveToken(tokenAddress, spenderAddress, amount)

            updateHistory(null, {
                ...updateData,
                currentStep: StepNames.APPROVE_TOKENS_PENDING,
                result: {
                    ...updateData.result,
                    message: 'Waiting for approval confirmation.',
                    sourceTxHash: `${getBlockExplorerUrlFromChainId(sellData.chainId)}/${hash}`,
                },
            })

            await waitForApproval(hash, sellData.chainId)
            logger.info('Token approval confirmed', { hash })
            return hash
        } catch (error: any) {
            if (error.message.includes('User rejected the request')) {
                logger.info('User rejected the approval transaction', { chainId: sellData.chainId })
                return ''
            }

            logger.error(`Token approval failed (chainId: ${sellData.chainId})`, error)
            throw new Error('Token approval failed. Please try again.')
        }
    }

    const handleApproval = async (
        updateData: UpdateData,
        sellData: IExchangeData,
        walletAddress: string
    ): Promise<string> => {
        try {
            const mappedAddress = mapTokenAddress(sellData.token?.address, true, +sellData.chainId, true)
            if (!mappedAddress) {
                throw new Error('Invalid token address')
            }
            const tokenAddress = isFeatureEnabled('USE_TESTNETS_ONLY')
                ? getChecksumAddress(mappedAddress)
                : mappedAddress

            const allowance = await getAllowance(+sellData.chainId, tokenAddress, walletAddress, swapContract.address)

            if (allowance >= sellData.value) {
                logger.info('Sufficient allowance exists', {
                    allowance,
                    required: sellData.value,
                })
                return 'Sufficient allowance exists'
            }

            updateHistory(null, {
                ...updateData,
                currentStep: StepNames.APPROVE_TOKENS,
                result: { ...updateData.result, message: 'Please approve tokens.' },
            })

            try {
                const maxUint256 = BigInt('0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff')
                const hash = await handleTokenApproval(
                    tokenAddress,
                    swapContract?.address,
                    maxUint256,
                    updateData,
                    sellData
                )
                return hash
            } catch (approvalError: any) {
                // Check for user rejection
                if (
                    approvalError?.message?.includes('User rejected') ||
                    approvalError?.message?.includes('User denied')
                ) {
                    return ''
                }

                // Handle other approval errors
                throw new Error(`Token approval failed: ${approvalError.message}`)
            }
        } catch (error: any) {
            logger.error(`Approval process failed (chainId: ${sellData.chainId})`, error)
            throw error
        }
    }

    const handleWrap = async (amount: bigint, updateData: UpdateData, sellData: IExchangeData): Promise<string> => {
        try {
            logger.info('Initiating wrap operation', {
                amount,
                tokenAddress: WRAPPED_GAS_TOKENS[sellData.chainId].address,
                currentChainId: chainId,
                targetChainId: sellData.chainId,
            })
            
            // Get native and wrapped token symbols for the chain
            const nativeSymbol = getGasTokenSymbol(sellData.chainId)
            const wrappedSymbol = `W${nativeSymbol}`
            
            // Track wrap attempt
            analyticsService.trackWrapAttempt(
                nativeSymbol,
                wrappedSymbol,
                sellData.chainId,
                amount.toString()
            )

            // Ensure user is on the correct chain
            await ensureCorrectChain(sellData.chainId, 'wrap', updateData, StepNames.DEPOSIT_WETH)

            updateHistory(null, {
                ...updateData,
                currentStep: StepNames.DEPOSIT_WETH,
                result: { ...updateData.result, message: `Depositing ${amount} gas.` },
            })

            const wrappedToken = WRAPPED_GAS_TOKENS[sellData.chainId]
            if (wrappedToken && isEVMAddress(wrappedToken.address)) {
                const tokenAddress = isFeatureEnabled('USE_TESTNETS_ONLY')
                    ? getChecksumAddress(wrappedToken.address)
                    : wrappedToken.address
                const hash = await depositWeth(tokenAddress, BigInt(amount))
                
                // Track wrap started
                analyticsService.trackWrapStarted(
                    nativeSymbol,
                    wrappedSymbol,
                    sellData.chainId,
                    amount.toString(),
                    hash
                )

                updateHistory(null, {
                    ...updateData,
                    currentStep: StepNames.DEPOSIT_WETH_PENDING,
                    result: {
                        ...updateData.result,
                        message: `Depositing ${amount} gas.`,
                        sourceTxHash: `${getBlockExplorerUrlFromChainId(sellData.chainId)}/${hash}`,
                    },
                })

                await waitForDeposit(hash, sellData.chainId)
                logger.success('Wrap operation completed', { hash })
                
                // Track wrap success
                analyticsService.trackWrapSuccess(
                    nativeSymbol,
                    wrappedSymbol,
                    sellData.chainId,
                    amount.toString(),
                    hash
                )

                return hash
            } else {
                logger.fatal(`No wrapped gas token contract found for chainId ${sellData.chainId}`)
                
                // Track wrap failed
                analyticsService.trackWrapFailed(
                    nativeSymbol,
                    wrappedSymbol,
                    sellData.chainId,
                    amount.toString(),
                    `No wrapped gas token contract found for chainId ${sellData.chainId}`
                )
                
                throw new Error(
                    `Error wrapping gas asset: No wrapped gas token contract found for chainId ${sellData.chainId}`
                )
            }
        } catch (error: any) {
            // Check if this was a user rejection
            if (
                error?.message?.includes('rejected') ||
                error?.message?.includes('denied') ||
                error?.message?.includes('cancelled') ||
                error?.message?.includes('declined')
            ) {
                logger.info('User rejected the wrap transaction', { chainId: sellData.chainId })
                
                // Get token symbols for analytics
                const nativeSymbol = getGasTokenSymbol(sellData.chainId)
                const wrappedSymbol = `W${nativeSymbol}`
                
                // Track wrap failed - user rejection
                analyticsService.trackWrapFailed(
                    nativeSymbol,
                    wrappedSymbol,
                    sellData.chainId,
                    amount.toString(),
                    'User rejected the transaction'
                )
                
                // Return empty string or some indicator that user rejected
                return '' // or return a special value that your calling code can check
            }

            // For other errors, log and throw
            logger.error(`Wrap operation failed (chainId: ${sellData.chainId})`, error)
            
            // Get token symbols for analytics
            const nativeSymbol = getGasTokenSymbol(sellData.chainId)
            const wrappedSymbol = `W${nativeSymbol}`
            
            // Track wrap failed - other error
            analyticsService.trackWrapFailed(
                nativeSymbol,
                wrappedSymbol,
                sellData.chainId,
                amount.toString(),
                error.message || 'Unknown error'
            )
            
            throw new Error('Error wrapping gas asset. Please try again.', { cause: error })
        }
    }

    const handleUnwrap = async (amount: bigint, updateData: UpdateData, sellData: IExchangeData): Promise<string> => {
        try {
            logger.info('Initiating unwrap operation', {
                amount,
                tokenAddress: WRAPPED_GAS_TOKENS[sellData.chainId].address,
                currentChainId: chainId,
                targetChainId: sellData.chainId,
            })
            
            // Get token symbols
            const nativeSymbol = getGasTokenSymbol(sellData.chainId)
            const wrappedSymbol = `W${nativeSymbol}`
            
            // Track unwrap attempt
            analyticsService.trackUnwrapAttempt(
                wrappedSymbol,
                nativeSymbol,
                sellData.chainId,
                amount.toString()
            )

            // Ensure user is on the correct chain
            await ensureCorrectChain(sellData.chainId, 'unwrap', updateData, StepNames.UNWRAP_WETH)

            updateHistory(null, {
                ...updateData,
                currentStep: StepNames.UNWRAP_WETH,
                result: { ...updateData.result, message: `Unwrapping ${amount} gas.` },
            })

            const wrappedToken = WRAPPED_GAS_TOKENS[sellData.chainId]
            if (!wrappedToken || !isEVMAddress(wrappedToken.address)) {
                logger.fatal(`No wrapped gas token contract found for chainId ${sellData.chainId}`)
                throw new Error(
                    `Error unwrapping gas asset: No wrapped gas token contract found for chainId ${sellData.chainId}`
                )
            }

            const tokenAddress = isFeatureEnabled('USE_TESTNETS_ONLY')
                ? getChecksumAddress(wrappedToken.address)
                : wrappedToken.address

            const hash = await withdrawWeth(tokenAddress, BigInt(amount))
            
            // Track unwrap started
            analyticsService.trackUnwrapStarted(
                wrappedSymbol,
                nativeSymbol,
                sellData.chainId,
                amount.toString(),
                hash
            )
            
            logger.info('Updating history with currentStep=UNWRAP_WETH_PENDING...')
            updateHistory(null, {
                ...updateData,
                currentStep: StepNames.UNWRAP_WETH_PENDING,
                result: {
                    ...updateData.result,
                    message: `Unwrapping ${amount} gas.`,
                    sourceTxHash: `${getBlockExplorerUrlFromChainId(sellData.chainId)}/${hash}`,
                },
            })

            await waitForWithdraw(hash, sellData.chainId)
            logger.success('Unwrap operation completed', { hash })
            
            // Track unwrap success
            analyticsService.trackUnwrapSuccess(
                wrappedSymbol,
                nativeSymbol,
                sellData.chainId,
                amount.toString(),
                hash
            )

            return hash
        } catch (error: any) {
            logger.error('Unwrap operation failed', error)
            
            // Check if this was a user rejection
            const isUserRejection = error?.message?.includes('rejected') || 
                error?.message?.includes('denied') || 
                error?.message?.includes('cancelled') || 
                error?.message?.includes('declined');
            
            // Get token symbols for analytics
            const nativeSymbol = getGasTokenSymbol(sellData.chainId)
            const wrappedSymbol = `W${nativeSymbol}`
            
            // Track unwrap failed
            analyticsService.trackUnwrapFailed(
                wrappedSymbol,
                nativeSymbol,
                sellData.chainId,
                amount.toString(),
                isUserRejection ? 'User rejected the transaction' : (error.message || 'Unknown error')
            )
            
            throw new Error('Error unwrapping token. Please try again.')
        }
    }

    const handleCctpExchange = async (
        hash: string,
        exchangeId: string,
        sellData: IExchangeData,
        buyData: IExchangeData
    ) => {
        try {
            logger.info('Handling CCTP exchange', { hash })

            const result = await apiNewPostReceiveCctpMessage(
                availableNetworks[sellData.chainId].name,
                hash,
                availableNetworks[buyData.chainId].name?.toLowerCase() === 'solana' ? solanaWalletAddress : undefined
            )

            updateHistory(exchangeId, {
                currentStep: StepNames.MATCH_CREATED,
                result: {
                    status: ResultCode.pending,
                    message: 'Order accepted. Allow 5-20 minutes for matching.',
                    transactionETA: result?.eta || 20,
                    sourceTxHash: `${getBlockExplorerUrlFromChainId(sellData.chainId)}/${hash}`,
                },
            })

            return result
        } catch (error) {
            logger.error(`CCTP exchange failed (chainId: ${sellData.chainId})`, error)
            throw error
        }
    }

    const executeSwap = async (params: EvmSwapParams, referrer?: string): Promise<string> => {
        const { sellData, buyData, quote, exchangeId, liquiditySource, onSuccess } = params

        try {
            logger.info('Initiating EVM swap', { params })

            // Ensure user is on the correct chain
            await ensureCorrectChain(sellData.chainId, 'swap', null, StepNames.CONFIRM_SWAP)

            updateHistory(null, {
                exchangeId,
                currentStep: StepNames.CONFIRM_SWAP,
                result: {
                    status: ResultCode.pending,
                    message: 'Please confirm swap.',
                },
            })

            const exchangeType =
                liquiditySource === LiquiditySource.cctpDirect ? ExchangeType.cctp : ExchangeType.order_book
            const functionName = exchangeType === ExchangeType.cctp ? 'depositForBurn' : 'placeOrder'

            const functionParams = await getSwapFunctionParameters(
                exchangeType,
                destinationAddress,
                sellData,
                buyData,
                quote as IQuoteData,
                false,
                availableNetworks
            )

            logger.info('Using standard transaction for transaction', { functionName, args: functionParams })

            // Standard transaction without EIP-712
            const hash = await writeContractAsync({
                abi: swapContract.abi,
                address: swapContract.address,
                functionName,
                args: functionParams || [],
            })

            if (!hash) {
                throw new Error('Could not send transaction.')
            }

            updateHistory(null, {
                exchangeId,
                currentStep: StepNames.CONFIRM_SWAP_PENDING,
                result: {
                    status: ResultCode.pending,
                    message: 'Waiting for confirmation.',
                    sourceTxHash: `${getBlockExplorerUrlFromChainId(sellData.chainId)}/${hash}`,
                },
            })

            // Pass the chainId from sellData to the waitForTransactionReceipt function
            // This ensures we query the correct chain instead of searching across multiple chains
            const receipt: any = await waitForTransactionReceipt(hash, false, false, sellData.chainId)

            updateHistory(null, {
                exchangeId,
                currentStep: StepNames.MATCHING,
                result: {
                    status: ResultCode.pending,
                    message: 'Order matching in progress.',
                    transactionETA: 60,
                    sourceTxHash: `${getBlockExplorerUrlFromChainId(sellData.chainId)}/${hash}`,
                },
            })

            let result

            if (exchangeType === ExchangeType.cctp) {
                // For CCTP transactions, check if EIP-712 is supported
                if (supportsEip712(exchangeType)) {
                    try {
                        // Get function parameters from the previous call
                        const [amount, destinationDomain, mintRecipient, burnToken] = functionParams || []

                        // Prepare EIP-712 typed data for CCTP
                        const typedData = prepareCctpTypedData(
                            amount,
                            destinationDomain,
                            mintRecipient,
                            burnToken,
                            swapContract.address,
                            sellData.chainId
                        )

                        logger.info('Using EIP-712 typed data for CCTP transaction', {
                            typedData: JSON.stringify(typedData, null, 2),
                        })
                    } catch (signingError) {
                        logger.warn('CCTP EIP-712 signing failed, proceeding without signature', signingError)
                    }
                }

                result = await handleCctpExchange(hash, exchangeId, sellData, buyData)
            } else {
                const encodedTopics = encodeEventTopics({
                    abi: swapContract.abi,
                    eventName: 'OrderPlaced',
                })[0]

                const decodedData = processReceipt(
                    receipt,
                    encodedTopics,
                    availableNetworks[sellData.chainId].name,
                    swapContract.abi
                )

                try {
                    result = await apiSendOrderData(decodedData[0], referralCode.code, referrer)
                } catch (error) {
                    // First check if we're on testnet
                    if (isFeatureEnabled('USE_TESTNETS_ONLY')) {
                        // Only check for rate limiting on testnet
                        console.log('Checking rate limit on error:', error)
                        const isRateLimited =
                            (error as any)?.response?.status === 429 ||
                            (error as any)?.status === 429 ||
                            (error as Error)?.message?.includes('status code 429')

                        if (isRateLimited) {
                            console.log('Rate limited on testnet')
                            // Extract wait time if available
                            let waitTime = 30
                            const detail = (error as any)?.response?.data?.detail || (error as any)?.data?.detail

                            if (typeof detail === 'string') {
                                const match = detail.match(/Please wait (\d+\.?\d*) seconds/)
                                if (match && match[1]) {
                                    waitTime = Math.ceil(parseFloat(match[1]))
                                }
                            }

                            // Throw a specific rate limit error with wait time
                            throw new Error(`Rate limited. Please try again in ${waitTime} seconds.`)
                        }
                    }

                    // Default case for production or non-rate-limit errors
                    throw new Error('Cannot send order data')
                }
            }

            if (result?.status === ResultCode.failure) {
                // This error happens when order is placed on chain,
                // but backend cannot process it
                console.log('[useExchangeTokenEvm] Order data submission failed', {
                    exchangeType,
                    errorMessage: result.message,
                    errorObj: 'errorobj' in result ? result.errorobj : undefined,
                    responseDetails: 'response' in result && result.response ? result.response.data : undefined,
                    statusCode: 'response' in result && result.response ? result.response.status : undefined,
                })

                // Detailed error for debugging
                const errorDetails = JSON.stringify(
                    {
                        message: result.message,
                        errorObj: 'errorobj' in result ? result.errorobj : undefined,
                        responseData: 'response' in result && result.response ? result.response.data : undefined,
                        status: 'response' in result && result.response ? result.response.status : undefined,
                    },
                    null,
                    2
                )

                throw new Error(`Cannot send order data for ${exchangeType} exchange: ${result.message}`, {
                    cause: { ...result, details: errorDetails },
                })
            }

            updateHistory(exchangeId, {
                currentStep: StepNames.MATCH_CREATED,
                result: {
                    status: ResultCode.pending,
                    message: 'Order accepted. Order matching in-progress.',
                    transactionETA: 60,
                    sourceTxHash: `${getBlockExplorerUrlFromChainId(sellData.chainId)}/${hash}`,
                },
            })

            // Update Redux dispatch calls to Zustand
            setOrderId(result?.id?.toString() || '')

            // small delay to start timer, without instant Match Executed
            await new Promise(r => setTimeout(r, 250))

            updateHistory(exchangeId, {
                currentStep: StepNames.MATCH_EXECUTED,
                orderId: typeof result?.id === 'string' ? result.id : '',
                result: {
                    status: ResultCode.pending,
                    message: 'Order successfully sent.',
                    transactionETA: result?.eta || 10,
                    sourceTxHash: `${getBlockExplorerUrlFromChainId(sellData.chainId)}/${hash}`,
                },
            })

            onSuccess()
            return hash
        } catch (error: any) {
            if (error.message.includes('User rejected the request')) {
                logger.info('User rejected the swap transaction', { chainId: sellData.chainId })
                return ''
            }

            logger.error(`EVM swap failed (chainId: ${sellData.chainId}):`, error)
            throw error
        } finally {
            setWaitingForConnectedWalletInteraction(false)
        }
    }

    return {
        executeSwap,
        handleApproval,
        handleWrap,
        handleUnwrap,
    }
}
